import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { csSession } from "../../auth/session";
import { csMyPayments, csInitPayment } from "../../api/payments.api";
import { csMySubscription } from "../../api/subscription.api";
import { csIsInWhatsappGroup, csTryAddWhatsappGroup } from "../../api/whatsapp.api";
import { csMyReferral } from "../../api/referral.api";
import styles from "../../styles/login.module.css";
import moment from "moment-timezone";
import "moment/locale/az";

moment.locale("az");
const AZ_TZ = "Asia/Baku";

export default function Dashboard() {
  const user = csSession.getUser();
  const phone = csSession.getPhone();
  const nav = useNavigate();

  const [sub, setSub] = useState(null);
  const [payments, setPayments] = useState([]);
  const [ref, setRef] = useState(null);

  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const [inGroup, setInGroup] = useState(null);
  const [groupCheckErr, setGroupCheckErr] = useState("");

  const [refOpen, setRefOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  const logout = () => {
    csSession.clear();
    nav("/couriersub/login", { replace: true });
  };

  // 994551234567 -> 0551234567 (yalnız ekranda göstərmək üçün)
  function displayPhoneAz(phone) {
    const p = String(phone || "").replace(/\s+/g, "");
    if (!p) return "";
    if (p.startsWith("994") && p.length >= 12) return "0" + p.slice(3);
    if (p.startsWith("+994") && p.length >= 13) return "0" + p.slice(4);
    if (p.startsWith("0")) return p;
    return p;
  }
  const phoneUi = useMemo(() => displayPhoneAz(phone), [phone]);

  // ✅ BURDA 3 API-ni birlikdə yükləyirik
  useEffect(() => {
    if (!phone) return;

    let cancelled = false;

    const load = async () => {
      setMsg("");
      setLoading(true);
      try {
        const [subRes, payRes, refRes] = await Promise.all([
          csMySubscription(phone),
          csMyPayments(phone),
          csMyReferral(phone),
        ]);

        if (cancelled) return;

        const subPayload = subRes?.data?.data ?? subRes?.data ?? subRes ?? null;
        const payPayload = payRes?.data?.data ?? payRes?.data ?? payRes ?? [];
        const refPayload = refRes?.data?.data ?? refRes?.data ?? refRes ?? null;

        setSub(subPayload);
        setPayments(Array.isArray(payPayload) ? payPayload : []);
        setRef(refPayload);
      } catch (err) {
        if (cancelled) return;
        setMsg(err?.response?.data?.message || err?.message || "Məlumatlar yüklənmədi.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [phone]);

  useEffect(() => {
    const u = csSession.getUser();
    if (!u) {
      nav("/couriersub/login", { replace: true });
      return;
    }

    const verifyStatus = String(u?.verifyStatus || "").toUpperCase();

    // admin istisna:
    const rawPhone = String(u?.phone || csSession.getPhone() || "").replace(/\s+/g, "");
    let normalizedPhone = rawPhone;
    if (rawPhone.startsWith("994")) normalizedPhone = "0" + rawPhone.slice(3);
    if (rawPhone.startsWith("+994")) normalizedPhone = "0" + rawPhone.slice(4);

    if (normalizedPhone !== "0773134223" && verifyStatus === "PHONE_PENDING") {
      nav("/couriersub/verify", { replace: true });
    }
  }, [nav]);

  const subscriptionActive = useMemo(() => {
    const st = String(sub?.status || "").toUpperCase();
    return st === "ACTIVE";
  }, [sub]);

  // WhatsApp group check (yalnız ACTIVE olanda)
  useEffect(() => {
    if (!phone) return;
    if (!subscriptionActive) return;

    let cancelled = false;

    const run = async () => {
      setGroupCheckErr("");

      try {
        // 1) əvvəl check
        const first = await csIsInWhatsappGroup(phone);
        if (cancelled) return;

        const firstIn = Boolean(first?.inGroup);
        setInGroup(firstIn);

        // 2) qrupda deyilsə -> add attempt
        if (!firstIn) {
          try {
            await csTryAddWhatsappGroup(phone); // ✅ auto add cəhdi
          } catch (e) {
            // add attempt alınmasa belə, yenə check edək
          }

          // 3) yenidən check
          const second = await csIsInWhatsappGroup(phone);
          if (cancelled) return;

          setInGroup(Boolean(second?.inGroup));
        }
      } catch (e) {
        if (cancelled) return;
        setInGroup(null);
        setGroupCheckErr(e?.response?.data?.message || e?.message || "Qrup yoxlanılmadı.");
      }
    };

    run();
    return () => { cancelled = true; };
  }, [phone, subscriptionActive]);

  // ===== Referral UI helpers =====
  const refUrl = ref?.referralUrl || ""; // backend bunu qaytarsın
  const invitedCount = ref?.invitedCount ?? 0;
  const balanceAz = ref?.balanceInt ? (ref.balanceInt / 100).toFixed(2) : "0.00";
  const canWithdraw = (ref?.balanceInt || 0) > 0;

  const waText = useMemo(() => {
    const txt =
      `Salam. Daha çox Sifariş almaq isdəyirsiniz?. ` +
      `Dəvətimi qəbul edin.\n` +
      `${refUrl}`;
    return txt;
  }, [refUrl]);

  const openWhatsappShare = () => {
    const to = `https://wa.me/?text=${encodeURIComponent(waText)}`;
    window.open(to, "_blank");
  };

  const copyRefUrl = async () => {
    if (!refUrl) return;
    try {
      await navigator.clipboard.writeText(refUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      // fallback
      const ta = document.createElement("textarea");
      ta.value = refUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    }
  };

  const withdraw = () => {
    if (!canWithdraw) return;
    const admin = "994773134223";
    const text = `Salam. Mən dəvət balansımı çıxarmaq istəyirəm.\nTelefon: ${phoneUi}\nBalans: ${balanceAz} AZN`;
    window.open(`https://wa.me/${admin}?text=${encodeURIComponent(text)}`, "_blank");
  };

  // ===== Payment button (sənin mövcud logic) =====
  const nowAz = moment().tz(AZ_TZ);
  const now = new Date();
  const isFirstDay = now.getDate() === 1;

  function isPaidStatus(st) {
    const s = String(st || "").toUpperCase();
    return s === "PAID" || s === "SUCCESS" || s.includes("SUCCESS");
  }
  function toMoment(v) {
    if (!v) return null;
    let m = moment(v);
    if (m.isValid()) return m;
    const s = String(v).trim();
    m = moment(s, "YYYY [M]MM DD, HH:mm:ss", true);
    if (m.isValid()) return m;
    if (/^\d+$/.test(s)) {
      m = moment(Number(s));
      if (m.isValid()) return m;
    }
    return null;
  }

  const alreadyPaidThisMonth = useMemo(() => {
    const curKey = nowAz.format("YYYY-MM");
    return (payments || []).some((p) => {
      if (!isPaidStatus(p?.status)) return false;
      const paidAt = toMoment(p?.paidAt);
      if (!paidAt) return false;
      return paidAt.tz(AZ_TZ).format("YYYY-MM") === curKey;
    });
  }, [payments]);

  const payBtnEnabledForActive = subscriptionActive && isFirstDay && !alreadyPaidThisMonth;

  // Növbəti ödəniş tarixi (hər ayın 1-i)
  const nextPayDate = useMemo(() => {
    const m = moment().tz(AZ_TZ);

    // bu ayın 1-i
    const firstThisMonth = m.clone().startOf("month");

    // Əgər hələ ayın 1-i gəlməyibsə (praktikada olmur), bu ayın 1-i, yoxsa növbəti ayın 1-i
    const target = m.isBefore(firstThisMonth) ? firstThisMonth : firstThisMonth.clone().add(1, "month");

    return target.format("DD MMMM YYYY"); // məsələn: 01 aprel 2026
  }, []);

  const pay = async () => {
    setMsg("");
    try {
      setLoading(true);

      const res = await csInitPayment({
        phone,
        successUrl: "https://mototaksi.az/api/payments/epoint/success",
        errorUrl: "https://mototaksi.az/api/payments/epoint/error",
        description: "Kuryer abunəliyi (aylıq 5 AZN)",
      });

      const payload = res?.data?.data ?? res?.data ?? res;
      const url = payload?.paymentUrl || payload?.url;
      if (!url) throw new Error("Ödəniş linki gəlmədi.");

      window.location.href = url;
    } catch (err) {
      setMsg(err?.response?.data?.message || err?.message || "Ödəniş başlatmaq olmadı.");
      setLoading(false);
    }
  };

  // ===== UI =====
  const last3 = useMemo(() => (payments || []).slice(0, 3), [payments]);

  function mapStatusAz(st) {
    const s = String(st || "").toUpperCase();
    if (s === "INITIATED") return { text: "Gözləmədə", tone: "gray" };
    if (s === "SUCCESS" || s === "PAID") return { text: "Uğurlu", tone: "green" };
    if (s === "FAILED") return { text: "Uğursuz", tone: "red" };
    return { text: s || "-", tone: "gray" };
  }

  function formatAzDateTimeCompact(v) {
    const m = toMoment(v);
    if (!m) return "-";
    return m.tz(AZ_TZ).format("DD MMMM YYYY HH:mm:ss");
  }

  return (
    <div className={styles.page}>
      <div className={styles.card} style={{ maxWidth: 560 }}>
        <div className={styles.header}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
            <div className={styles.brand}>MotoTaksi.az</div>

            <button
              type="button"
              onClick={logout}
              style={{
                background: "transparent",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                padding: "6px 12px",
                fontSize: 13,
                cursor: "pointer",
              }}
            >
              Çıxış
            </button>
          </div>

          <h1 className={styles.title}>Kuryer Paneli</h1>

          <div className={styles.userLine}>
            <div className={styles.userName}>{user?.firstName || ""} {user?.lastName || ""}</div>
            <div className={styles.userPhone}>{phoneUi}</div>
          </div>
        </div>

        <div className={styles.form} style={{ gap: 12 }}>
          {msg && <div className={styles.error}>{msg}</div>}

          {/* ✅ REFERRAL BOX 
          <div className={styles.box}>
            <div className={styles.boxTitle}>Dostunu dəvət et</div>

            <button
              type="button"
              className={styles.btn}
              style={{ width: "100%" }}
              onClick={() => setRefOpen(true)}
              disabled={!refUrl}
              title={!refUrl ? "Referral məlumatı yüklənir..." : ""}
            >
              Dostunu dəvət et • 1 AZN qazan
            </button>

            <div className={styles.infoLine} style={{ marginTop: 10 }}>
              Dəvət etdiklərin: <b>{invitedCount}</b> · Balans: <b>{balanceAz} AZN</b>
            </div>

            <button
              type="button"
              className={styles.btnOutline}
              style={{ width: "100%", marginTop: 10, opacity: canWithdraw ? 1 : 0.6 }}
              onClick={withdraw}
              disabled={!canWithdraw}
            >
              Pul çıxarmaq üçün əlaqə
            </button>
          </div> */}

          {/* Abunəlik box (sənin əvvəlki kimi) */}
          <div className={styles.box}>
            <div className={styles.boxTitle}>Abunəlik</div>

            <div className={styles.rowBetween}>
              <span className={subscriptionActive ? styles.pillGreen : styles.pillRed}>
                {subscriptionActive ? "AKTİV" : "AKTİV DEYİL"}
              </span>
              <span className={styles.muted}>
                {sub?.plan === "MONTHLY_5" ? "Aylıq 5 AZN" : (sub?.plan || "")}
              </span>
            </div>

            {subscriptionActive && inGroup === false && (
              <div className={styles.warn} style={{ marginTop: 10 }}>
                <b>WhatsApp qrupuna əlavə edilə bilməmisiniz.</b> Zəhmət olmasa adminlə əlaqə saxlayın.

                <button
                  type="button"
                  className={styles.btnOutline}
                  style={{ width: "100%", marginTop: 10 }}
                  onClick={() => {
                    const admin = "994773134223";
                    const text = "Salam. Mən ödəniş etdim ama, whatsapp qrupuna əlavə edilə bilmədim.";
                    window.open(`https://wa.me/${admin}?text=${encodeURIComponent(text)}`, "_blank");
                  }}
                >
                  Admin ilə əlaqə
                </button>
              </div>
            )}

            {subscriptionActive && groupCheckErr && (
              <div className={styles.muted} style={{ marginTop: 8 }}>
                Qrup yoxlaması: {groupCheckErr}
              </div>
            )}

            {/* ✅ Ödəniş info (buttonun yuxarısında) */}
            {!subscriptionActive ? (
              <div className={styles.muted} style={{ marginTop: 10 }}>
                <b>Diqqət:</b> Hər ayın 1-i ödəniş alınacaq!
              </div>
            ) : (
              <div
                className={styles.muted}
                style={{
                  marginTop: 10,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <span><b>Növbəti ödəniş tarixi</b></span>
                <span style={{ fontWeight: 700 }}>{nextPayDate}</span>
              </div>
            )}

            <button
              className={styles.btn}
              type="button"
              onClick={pay}
              disabled={loading || (subscriptionActive ? !payBtnEnabledForActive : false)}
              style={{ width: "100%", marginTop: 12, opacity: subscriptionActive && !payBtnEnabledForActive ? 0.6 : 1 }}
            >
              {loading ? "Yönləndirilir..." : "5 AZN Ödə"}
            </button>

            {subscriptionActive && alreadyPaidThisMonth && (
              <div className={styles.muted} style={{ marginTop: 8 }}>
                Bu ay üçün ödəniş edilib.
              </div>
            )}
          </div>

          {/* Son 3 ödəniş */}
          <div className={styles.box}>
            <div className={styles.boxTitle}>Son ödənişlər</div>

            {last3.length === 0 ? (
              <div className={styles.muted}>Hələ ödəniş yoxdur.</div>
            ) : (
              <div className={styles.payList}>
                {last3.map((p) => {
                  const st = mapStatusAz(p.status);
                  const pill =
                    st.tone === "green" ? styles.pillGreen :
                      st.tone === "red" ? styles.pillRed :
                        styles.pillGray;

                  return (
                    <div key={p.id} className={styles.payRow}>
                      <div className={styles.payLeft}>
                        <div className={styles.payAmount}>
                          {(p.amountInt ? (p.amountInt / 100).toFixed(2) : "5.00")} {p.currency || "AZN"}
                        </div>
                        <div className={styles.payDate}>
                          {p.paidAt ? formatAzDateTimeCompact(p.paidAt) : formatAzDateTimeCompact(p.createdAt)}
                        </div>
                      </div>

                      <span className={pill}>{st.text}</span>
                    </div>
                  );
                })}
              </div>
            )}

            <div style={{ marginTop: 10 }}>
              <Link className={styles.link} to="/couriersub/payments">
                Bütün ödənişlər
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ MODAL */}
      {refOpen && (
        <div
          onClick={() => setRefOpen(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "grid",
            placeItems: "center",
            padding: 16,
            zIndex: 9999,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 520,
              background: "#fff",
              borderRadius: 16,
              border: "1px solid #e5e7eb",
              padding: 14,
              display: "grid",
              gap: 10,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center" }}>
              <div style={{ fontWeight: 900, fontSize: 16 }}>Dəvət linkin</div>
              <button className={styles.btnGhost} type="button" onClick={() => setRefOpen(false)}>
                Bağla
              </button>
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <input
                className={styles.input}
                value={refUrl}
                readOnly
                style={{ flex: 1 }}
              />
              <button className={styles.btnOutline} type="button" onClick={copyRefUrl} disabled={!refUrl}>
                {copied ? "Kopyalandı" : "Kopyala"}
              </button>
            </div>

            <button className={styles.btn} type="button" onClick={openWhatsappShare} disabled={!refUrl}>
              WhatsApp ilə göndər
            </button>

            <div className={styles.muted} style={{ fontSize: 12 }}>
              Qeyd: Dəvət sayılması üçün dəvət olunan şəxs qeydiyyat → telefon doğrulama → sənəd yükləmə →
              admin təsdiqi → 5 AZN ödəniş mərhələlərini tamamlamalıdır.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

